import React, { useEffect, useMemo, useState } from 'react'; // Add useState import
import { Checkbox } from '@components/ui/checkbox';
import { Label } from '@components/ui/label';
import { FormCard } from '@features/onboarding/components/shared/form-card/FormCard';
import { UseFormReturn } from 'react-hook-form';
import { useOnboardingStore } from '@features/onboarding/store/onboarding';
import { useApplicationStore } from 'src/store/application';
import { useInsuranceStore } from 'src/store/insurance';

interface CoverageTagsProps {
  form: UseFormReturn<Record<string, unknown>>;
  extraDependents: Array<{ firstName?: string; applicant: string }>;
  planType: string; // 'medical', 'dental', 'vision', etc.
  showTags: boolean;
  categoryPlans?: Array<{ id: string; category?: string }>; // For non-medical plans
}

export const CoverageTags = ({
  form,
  extraDependents,
  planType,
  showTags,
  categoryPlans = [],
}: CoverageTagsProps) => {
  const { setMedicalTags, setPlanTags, medicalTags, planTags } = useOnboardingStore();
  const { activeCategories, isMainPlanRejected, applications } = useApplicationStore();
  const { plans } = useInsuranceStore();

  const categoryKey = planType?.toLowerCase().replace(/[^a-zA-Z0-9]/g, '') || '';
  const planKey = `${categoryKey}Plan`;
  const selectedNonMedicalPlanId = form.getValues(planKey) as unknown as string | undefined;

  // Map local dependent index -> global applicant index in application.applicants
  const getGlobalApplicantIndex = (depIdx: number): number | null => {
    const app = applications?.[0] as any;
    const appApplicants: Array<{ applicant?: string; firstName?: string }> = app?.applicants || [];
    const dep = extraDependents[depIdx];
    if (!dep) return null;

    // Try exact match by role and firstName
    const exactIdx = appApplicants.findIndex(
      (a) => a?.applicant === dep.applicant && (!!dep.firstName ? a?.firstName === dep.firstName : true)
    );
    if (exactIdx >= 0) return exactIdx;

    // Fallback: role-based occurrence order among dependents
    if (dep.applicant === 'spouse') {
      const spouseIdx = appApplicants.findIndex((a) => a?.applicant === 'spouse');
      if (spouseIdx >= 0) return spouseIdx;
    }
    if (dep.applicant === 'dependent') {
      // Count how many dependents are before this one in extraDependents
      const dependentOccurence = extraDependents
        .slice(0, depIdx + 1)
        .filter((d) => d.applicant === 'dependent').length;

      // Now find the nth dependent in the main application list
      let count = 0;
      for (let i = 0; i < appApplicants.length; i++) {
        if (appApplicants[i]?.applicant === 'dependent') {
          count++;
          if (count === dependentOccurence) return i;
        }
      }
    }
    return null;
  };

  const tagsFromDependents = extraDependents.map((dep, index) => {
    const mapped = getGlobalApplicantIndex(index);
    const gid = mapped != null ? mapped : index + 1; // fallback: primary=0, dependents start at 1
    return {
      label: dep.firstName || `Dependent ${index + 1}`,
      value: `${dep.applicant}_${gid}`,
    };
  });

  // Initialize local state for checked tags as a map (keys must match tagsFromDependents values)
  const [checkedTags, setCheckedTags] = useState<Record<string, boolean>>(
    tagsFromDependents.reduce((acc, tag) => {
      acc[tag.value] = false;
      return acc;
    }, {} as Record<string, boolean>)
  );

  const isPlanBlocked = () => {
    if (planType === 'medical') {
      return isMainPlanRejected;
    } else {
      const categoryPlansFromStore = plans.filter((plan) => {
        const category = plan.category?.toLowerCase();
        return category === planType;
      });

      return categoryPlansFromStore.some((plan) =>
        activeCategories.includes(plan.category)
      );
    }
  };

  if (!showTags) return null;

  const getTitle = () => {
    switch (planType) {
      case 'medical':
        return 'Who do you want covered on this plan besides yourself?';
      case 'dental':
        return 'Who do you want covered on this dental plan besides yourself?';
      case 'vision':
        return 'Who do you want covered on this vision plan besides yourself?';
      case 'accident':
        return 'Who do you want covered on this accident plan besides yourself?';
      case 'critical illness':
        return 'Who do you want covered on this critical illness plan besides yourself?';
      default:
        return `Who do you want covered on this ${planType} plan besides yourself?`;
    }
  };

  const handleTagChange = (itemValue: string, checked: boolean) => {
    if (isPlanBlocked()) return;

    // Update local checkedTags state
    setCheckedTags((prev) => ({
      ...prev,
      [itemValue]: checked,
    }));

    // Sync with form and store (maintain existing behavior)
    if (planType === 'medical') {
      const current =
        (form.getValues('medicalTags') as unknown as string[]) || [];
      const newTags = checked
        ? [...current, itemValue]
        : current.filter((val: string) => val !== itemValue);

      form.setValue('medicalTags', newTags);
      setMedicalTags(newTags);
    } else {
      const planTagsForm =
        (form.getValues('planTags') as unknown as Record<string, string[]>) ||
        {};

      const selectedPlan = categoryPlans.find((plan) => {
        const categoryKey =
          plan.category?.toLowerCase().replace(/[^a-zA-Z0-9]/g, '') || '';
        const planKey = `${categoryKey}Plan`;
        const selectedPlanUid = form.getValues(planKey) as unknown as
          | string
          | null;
        return selectedPlanUid === plan.id;
      });

      if (selectedPlan) {
        const currentTags = planTagsForm[selectedPlan.id] || [];
        const newTags = checked
          ? [...currentTags, itemValue]
          : currentTags.filter((val: string) => val !== itemValue);

        planTagsForm[selectedPlan.id] = newTags;
        form.setValue('planTags', planTagsForm);
        setPlanTags(selectedPlan.id, newTags); // Sync with store
      } else {
        categoryPlans.forEach((plan) => {
          const currentTags = planTagsForm[plan.id] || [];
          const newTags = checked
            ? [...currentTags, itemValue]
            : currentTags.filter((val: string) => val !== itemValue);

          planTagsForm[plan.id] = newTags;
          setPlanTags(plan.id, newTags); // Sync with store
        });
        form.setValue('planTags', planTagsForm);
      }

      form.trigger();
    }
  };

  // getGlobalApplicantIndex defined above
  // const activeGlobalIdsForDebug = useMemo(() => {
  //   const ids: number[] = [];
  //   tagsFromDependents.forEach((tag, idx) => {
  //     if (checkedTags[tag.value]) { // Use checkedTags state
  //       const mapped = getGlobalApplicantIndex(idx);
  //       const gid = mapped != null ? mapped : idx + 1; // fallback: primary=0, dependents start at 1
  //       ids.push(gid);
  //     }
  //   });
  //   return ids;
  // }, [tagsFromDependents, extraDependents, applications, checkedTags]);

  // Seed checked states from application plans on mount/when selection changes
  useEffect(() => {
    const app = applications?.[0] as any;
    if (!app) return;

    const appPlans: any[] = app.plans || [];

    let applicantIdxs: number[] = [];
    if (planType === 'medical') {
      const medicalPlan = appPlans.find((p) => {
        const details = plans.find((pl) => String(pl.id) === String(p.id));
        return details?.category === 'Major Medical';
      });
      applicantIdxs = Array.isArray(medicalPlan?.applicants) ? medicalPlan.applicants : [];
    } else {
      if (selectedNonMedicalPlanId) {
        const idxSelectedNonMedicalPlanId = plans.find(e => e.uid === selectedNonMedicalPlanId)?.id;
        const selected = appPlans.find((p) => String(p.id) === String(idxSelectedNonMedicalPlanId));
        applicantIdxs = Array.isArray(selected?.applicants) ? selected.applicants : [];
      } else {
        const idxSet = new Set<number>();
        categoryPlans.forEach((cp) => {
          const found = appPlans.find((p) => String(p.id) === String(cp.id));
          (Array.isArray(found?.applicants) ? found.applicants : []).forEach((i: number) => idxSet.add(i));
        });
        applicantIdxs = Array.from(idxSet);
      }
    }

    const mapApplicantIdxs = applicantIdxs.filter(e => e).map(e => {
      if (e === 1) return `spouse_1`;
      return `dependent_${e}`;
    });
    mapApplicantIdxs.forEach(e => {
      setCheckedTags((prev) => ({
        ...prev,
        [`${e}`]: true,
      }));
    });
  }, [applications]);

  return (
    <FormCard imageSrc="/images/dependents.svg" title={getTitle()}>
      <div className="flex gap-5 items-center max-2xs:flex-col max-2xs:items-start max-2xs:gap-2">
        {/* <pre>{JSON.stringify(activeGlobalIdsForDebug, null, 2)}</pre> */}
        <p className="font-bold text-xl max-2xs:text-lg min-w-fit">You and</p>
        <div className="grid gap-x-16 grid-cols-1 xs:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 pr-4">
          {tagsFromDependents.map((item) => (
            <div key={item.value} className="flex items-center gap-2">
              <Checkbox
                id={`${planType}_${item.value}`}
                checked={checkedTags[item.value]} // Use checkedTags state
                onCheckedChange={(checked) =>
                  handleTagChange(item.value, !!checked)
                }
                className={isPlanBlocked() ? 'hidden' : ''}
              />
              <Label htmlFor={`${planType}_${item.value}`}>{item.label}</Label>
            </div>
          ))}
        </div>
      </div>
    </FormCard>
  );
};
